import React, { useState, useEffect } from 'react';
import { getThemeColors, spacing, typography, borderRadius, getShadowStyle } from '../../utils/theme';
import UserDashboardHeader from '../../components/UserDashboardHeader';

interface PaymentMethod {
  id: string;
  type: 'mpesa' | 'bank' | 'card';
  name: string;
  details: string;
  isDefault: boolean;
  isActive: boolean;
  lastUsed?: string;
}

interface MethodType {
  id: 'mpesa' | 'bank' | 'card';
  name: string;
  icon: string;
  color: string;
  description: string;
}

const PaymentMethods: React.FC = () => {
  const colors = getThemeColors('dark');

  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedMethodType, setSelectedMethodType] = useState<'mpesa' | 'bank' | 'card'>('mpesa');
  const [newMethodData, setNewMethodData] = useState({
    mpesa: { phoneNumber: '', name: '' },
    bank: { accountNumber: '', bankName: '', accountName: '' },
    card: { cardNumber: '', expiryDate: '', cardholderName: '' },
  });

  const methodTypes: MethodType[] = [
    {
      id: 'mpesa',
      name: 'M-Pesa',
      icon: '📱',
      color: '#00A651',
      description: 'Mobile money payments',
    },
    {
      id: 'bank',
      name: 'Bank Account',
      icon: '🏦',
      color: '#1976D2',
      description: 'Direct bank transfers',
    },
    {
      id: 'card',
      name: 'Debit/Credit Card',
      icon: '💳',
      color: '#FF6B35',
      description: 'Visa, Mastercard, etc.',
    },
  ];

  useEffect(() => {
    loadPaymentMethods();
  }, []);

  const loadPaymentMethods = async () => {
    try {
      setLoading(true);
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));

      const mockMethods: PaymentMethod[] = [
        {
          id: '1',
          type: 'mpesa',
          name: 'M-Pesa',
          details: '+254712345678',
          isDefault: true,
          isActive: true,
          lastUsed: '2024-01-15',
        },
        {
          id: '2',
          type: 'bank',
          name: 'KCB Bank',
          details: '****1234',
          isDefault: false,
          isActive: true,
          lastUsed: '2024-01-10',
        },
      ];
      setPaymentMethods(mockMethods);
    } catch (error) {
      console.error('Failed to load payment methods:', error);
      alert('Failed to load payment methods. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddPaymentMethod = async () => {
    // Basic validation
    if (selectedMethodType === 'mpesa' && !newMethodData.mpesa.phoneNumber) {
      alert('Please enter your M-Pesa phone number.');
      return;
    }
    if (selectedMethodType === 'bank' && (!newMethodData.bank.accountNumber || !newMethodData.bank.bankName)) {
      alert('Please fill in all bank account details.');
      return;
    }
    if (selectedMethodType === 'card' && (!newMethodData.card.cardNumber || !newMethodData.card.expiryDate)) {
      alert('Please fill in all card details.');
      return;
    }

    try {
      setLoading(true);
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));

      const newMethod: PaymentMethod = {
        id: Date.now().toString(),
        type: selectedMethodType,
        name: methodTypes.find(t => t.id === selectedMethodType)?.name || '',
        details: getMethodDisplayDetails(selectedMethodType, newMethodData[selectedMethodType]),
        isDefault: paymentMethods.length === 0,
        isActive: true,
        lastUsed: undefined,
      };

      setPaymentMethods(prev => [...prev, newMethod]);
      setShowAddModal(false);
      resetNewMethodData();

      alert('Payment method added successfully!');
    } catch (error) {
      console.error('Failed to add payment method:', error);
      alert('Failed to add payment method. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getMethodDisplayDetails = (type: string, data: any) => {
    switch (type) {
      case 'mpesa':
        return data.phoneNumber;
      case 'bank':
        return `${data.bankName} - ****${data.accountNumber.slice(-4)}`;
      case 'card':
        return `****${data.cardNumber.slice(-4)}`;
      default:
        return '';
    }
  };

  const resetNewMethodData = () => {
    setNewMethodData({
      mpesa: { phoneNumber: '', name: '' },
      bank: { accountNumber: '', bankName: '', accountName: '' },
      card: { cardNumber: '', expiryDate: '', cardholderName: '' },
    });
  };

  const handleSetDefault = async (methodId: string) => {
    try {
      setPaymentMethods(prev =>
        prev.map(method => ({
          ...method,
          isDefault: method.id === methodId,
        }))
      );
      alert('Default payment method updated!');
    } catch (error) {
      console.error('Failed to set default payment method:', error);
      alert('Failed to update default payment method.');
    }
  };

  const handleToggleActive = async (methodId: string, isActive: boolean) => {
    try {
      setPaymentMethods(prev =>
        prev.map(method =>
          method.id === methodId ? { ...method, isActive } : method
        )
      );
    } catch (error) {
      console.error('Failed to toggle payment method:', error);
      alert('Failed to update payment method status.');
    }
  };

  const handleDeleteMethod = (methodId: string) => {
    const method = paymentMethods.find(m => m.id === methodId);

    if (window.confirm(`Are you sure you want to delete ${method?.name} (${method?.details})?`)) {
      try {
        setPaymentMethods(prev => prev.filter(m => m.id !== methodId));
        alert('Payment method deleted successfully!');
      } catch (error) {
        console.error('Failed to delete payment method:', error);
        alert('Failed to delete payment method.');
      }
    }
  };

  const styles: { [key: string]: React.CSSProperties } = {
    container: {
      minHeight: '100vh',
      backgroundColor: colors.background,
      paddingBottom: 80, // Space for tab bar
    },
    scrollableContent: {
      flex: 1,
      overflowY: 'auto',
      paddingTop: 80, // Space for fixed header
      paddingBottom: 80, // Space for fixed tab bar
    },
    content: {
      maxWidth: '900px',
      margin: '0 auto',
      padding: spacing.lg,
    },
    header: {
      backgroundColor: colors.surface,
      borderRadius: borderRadius.xl,
      padding: spacing.xl,
      marginBottom: spacing.xl,
      border: `1px solid ${colors.border}`,
      ...getShadowStyle('lg'),
      textAlign: 'center',
    },
    headerIcon: {
      fontSize: 48,
      marginBottom: spacing.md,
    },
    headerTitle: {
      fontSize: typography.fontSize.xl,
      fontWeight: typography.fontWeight.bold,
      color: colors.text,
      marginBottom: spacing.sm,
    },
    headerSubtitle: {
      fontSize: typography.fontSize.base,
      color: colors.textSecondary,
    },
    addButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: `${spacing.lg} ${spacing.xl}`,
      borderRadius: borderRadius.lg,
      backgroundColor: colors.primary,
      color: colors.white,
      border: 'none',
      cursor: 'pointer',
      fontSize: typography.fontSize.lg,
      fontWeight: typography.fontWeight.bold,
      marginBottom: spacing.xl,
      ...getShadowStyle('md'),
      transition: 'all 0.3s ease',
    },
    methodsList: {
      display: 'flex',
      flexDirection: 'column',
      gap: spacing.lg,
    },
    methodCard: {
      backgroundColor: colors.surface,
      borderRadius: borderRadius.xl,
      padding: spacing.xl,
      border: `1px solid ${colors.border}`,
      ...getShadowStyle('lg'),
    },
    methodHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: spacing.lg,
    },
    methodInfo: {
      display: 'flex',
      alignItems: 'center',
      flex: 1,
    },
    methodIcon: {
      width: 56,
      height: 56,
      borderRadius: 28,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginRight: spacing.lg,
      fontSize: 24,
    },
    methodDetails: {
      flex: 1,
    },
    methodName: {
      fontSize: typography.fontSize.lg,
      fontWeight: typography.fontWeight.bold,
      color: colors.text,
      marginBottom: spacing.sm,
    },
    methodDetailsText: {
      fontSize: typography.fontSize.base,
      color: colors.textSecondary,
      marginBottom: spacing.sm,
    },
    defaultBadge: {
      fontSize: typography.fontSize.sm,
      fontWeight: typography.fontWeight.bold,
      color: colors.primary,
      textTransform: 'uppercase',
    },
    toggle: {
      width: 60,
      height: 32,
      borderRadius: 16,
      padding: 2,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      position: 'relative',
    },
    toggleActive: {
      backgroundColor: colors.primary,
    },
    toggleInactive: {
      backgroundColor: colors.border,
    },
    toggleCircle: {
      width: 28,
      height: 28,
      borderRadius: 14,
      backgroundColor: colors.white,
      position: 'absolute',
      top: 2,
      transition: 'all 0.3s ease',
    },
    toggleCircleActive: {
      right: 2,
    },
    toggleCircleInactive: {
      left: 2,
    },
    methodActions: {
      display: 'flex',
      gap: spacing.md,
      marginTop: spacing.lg,
    },
    actionButton: {
      flex: 1,
      padding: `${spacing.md} ${spacing.lg}`,
      borderRadius: borderRadius.lg,
      border: 'none',
      cursor: 'pointer',
      fontSize: typography.fontSize.base,
      fontWeight: typography.fontWeight.bold,
      ...getShadowStyle('sm'),
      transition: 'all 0.2s ease',
    },
    primaryButton: {
      backgroundColor: colors.primary,
      color: colors.white,
    },
    dangerButton: {
      backgroundColor: colors.error,
      color: colors.white,
    },
    emptyState: {
      backgroundColor: colors.surface,
      borderRadius: borderRadius.xl,
      padding: spacing.xxxl,
      border: `1px solid ${colors.border}`,
      ...getShadowStyle('lg'),
      textAlign: 'center',
    },
    emptyIcon: {
      fontSize: 64,
      marginBottom: spacing.lg,
      opacity: 0.5,
    },
    emptyText: {
      fontSize: typography.fontSize.lg,
      fontWeight: typography.fontWeight.bold,
      color: colors.textSecondary,
      marginBottom: spacing.sm,
    },
    emptySubtext: {
      fontSize: typography.fontSize.base,
      color: colors.textSecondary,
      lineHeight: 1.5,
    },
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    },
    modalContent: {
      backgroundColor: colors.surface,
      borderRadius: borderRadius.xl,
      width: '90%',
      maxWidth: 500,
      maxHeight: '90vh',
      overflow: 'hidden',
      ...getShadowStyle('xl'),
    },
    modalHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: spacing.lg,
      borderBottom: `1px solid ${colors.border}`,
    },
    modalTitle: {
      fontSize: typography.fontSize.xl,
      fontWeight: typography.fontWeight.bold,
      color: colors.text,
    },
    modalButtons: {
      display: 'flex',
      gap: spacing.md,
    },
    modalButton: {
      padding: `${spacing.sm} ${spacing.md}`,
      borderRadius: borderRadius.md,
      fontSize: typography.fontSize.base,
      fontWeight: typography.fontWeight.bold,
      cursor: 'pointer',
      border: 'none',
    },
    cancelButton: {
      backgroundColor: 'transparent',
      color: colors.primary,
    },
    saveButton: {
      backgroundColor: colors.primary,
      color: colors.white,
    },
    modalBody: {
      padding: spacing.lg,
      maxHeight: 400,
      overflowY: 'auto',
    },
    sectionTitle: {
      fontSize: typography.fontSize.xl,
      fontWeight: typography.fontWeight.bold,
      color: colors.text,
      marginBottom: spacing.lg,
      paddingBottom: spacing.md,
      borderBottom: `2px solid ${colors.primary}`,
    },
    methodTypesGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
      gap: spacing.md,
      marginBottom: spacing.xl,
    },
    methodTypeCard: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: spacing.lg,
      borderRadius: borderRadius.lg,
      border: `2px solid ${colors.border}`,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      backgroundColor: colors.surface,
      ...getShadowStyle('md'),
    },
    methodTypeCardSelected: {
      borderColor: colors.primary,
      backgroundColor: colors.primary + '10',
      transform: 'scale(1.05)',
    },
    methodTypeIcon: {
      fontSize: 32,
      marginBottom: spacing.sm,
    },
    methodTypeName: {
      fontSize: typography.fontSize.base,
      fontWeight: typography.fontWeight.bold,
      color: colors.text,
      marginBottom: spacing.sm,
      textAlign: 'center',
    },
    methodTypeDescription: {
      fontSize: typography.fontSize.sm,
      color: colors.textSecondary,
      textAlign: 'center',
      lineHeight: 1.4,
    },
    inputGroup: {
      marginBottom: spacing.lg,
    },
    inputLabel: {
      fontSize: typography.fontSize.base,
      fontWeight: typography.fontWeight.bold,
      color: colors.text,
      marginBottom: spacing.sm,
    },
    input: {
      width: '100%',
      padding: spacing.md,
      borderRadius: borderRadius.md,
      border: `1px solid ${colors.border}`,
      backgroundColor: colors.background,
      color: colors.text,
      fontSize: typography.fontSize.base,
      outline: 'none',
    },
  };

  const renderToggle = (isActive: boolean) => (
    <div
      style={{
        ...styles.toggle,
        ...(isActive ? styles.toggleActive : styles.toggleInactive),
      }}
    >
      <div
        style={{
          ...styles.toggleCircle,
          ...(isActive ? styles.toggleCircleActive : styles.toggleCircleInactive),
        }}
      />
    </div>
  );

  const renderPaymentMethod = (method: PaymentMethod) => {
    const methodType = methodTypes.find(t => t.id === method.type);

    return (
      <div key={method.id} style={styles.methodCard}>
        <div style={styles.methodHeader}>
          <div style={styles.methodInfo}>
            <div
              style={{
                ...styles.methodIcon,
                backgroundColor: methodType?.color + '20',
                color: methodType?.color,
              }}
            >
              {methodType?.icon}
            </div>
            <div style={styles.methodDetails}>
              <div style={styles.methodName}>{method.name}</div>
              <div style={styles.methodDetailsText}>{method.details}</div>
              {method.isDefault && (
                <div style={styles.defaultBadge}>Default</div>
              )}
            </div>
          </div>
          <div
            onClick={() => handleToggleActive(method.id, !method.isActive)}
          >
            {renderToggle(method.isActive)}
          </div>
        </div>

        <div style={styles.methodActions}>
          {!method.isDefault && (
            <button
              style={{ ...styles.actionButton, ...styles.primaryButton }}
              onClick={() => handleSetDefault(method.id)}
            >
              Set as Default
            </button>
          )}

          <button
            style={{ ...styles.actionButton, ...styles.dangerButton }}
            onClick={() => handleDeleteMethod(method.id)}
          >
            Delete
          </button>
        </div>
      </div>
    );
  };

  const renderAddMethodForm = () => {
    switch (selectedMethodType) {
      case 'mpesa':
        return (
          <div>
            <div style={styles.inputGroup}>
              <label style={styles.inputLabel}>Phone Number</label>
              <input
                type="tel"
                style={styles.input}
                placeholder="+254712345678"
                value={newMethodData.mpesa.phoneNumber}
                onChange={(e) => setNewMethodData(prev => ({
                  ...prev,
                  mpesa: { ...prev.mpesa, phoneNumber: e.target.value }
                }))}
              />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.inputLabel}>Account Name (Optional)</label>
              <input
                type="text"
                style={styles.input}
                placeholder="John Doe"
                value={newMethodData.mpesa.name}
                onChange={(e) => setNewMethodData(prev => ({
                  ...prev,
                  mpesa: { ...prev.mpesa, name: e.target.value }
                }))}
              />
            </div>
          </div>
        );

      case 'bank':
        return (
          <div>
            <div style={styles.inputGroup}>
              <label style={styles.inputLabel}>Bank Name</label>
              <input
                type="text"
                style={styles.input}
                placeholder="KCB Bank"
                value={newMethodData.bank.bankName}
                onChange={(e) => setNewMethodData(prev => ({
                  ...prev,
                  bank: { ...prev.bank, bankName: e.target.value }
                }))}
              />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.inputLabel}>Account Number</label>
              <input
                type="text"
                style={styles.input}
                placeholder="1234567890"
                value={newMethodData.bank.accountNumber}
                onChange={(e) => setNewMethodData(prev => ({
                  ...prev,
                  bank: { ...prev.bank, accountNumber: e.target.value }
                }))}
              />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.inputLabel}>Account Name</label>
              <input
                type="text"
                style={styles.input}
                placeholder="John Doe"
                value={newMethodData.bank.accountName}
                onChange={(e) => setNewMethodData(prev => ({
                  ...prev,
                  bank: { ...prev.bank, accountName: e.target.value }
                }))}
              />
            </div>
          </div>
        );

      case 'card':
        return (
          <div>
            <div style={styles.inputGroup}>
              <label style={styles.inputLabel}>Card Number</label>
              <input
                type="text"
                style={styles.input}
                placeholder="1234 5678 9012 3456"
                value={newMethodData.card.cardNumber}
                onChange={(e) => setNewMethodData(prev => ({
                  ...prev,
                  card: { ...prev.card, cardNumber: e.target.value }
                }))}
                maxLength={19}
              />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.inputLabel}>Expiry Date</label>
              <input
                type="text"
                style={styles.input}
                placeholder="MM/YY"
                value={newMethodData.card.expiryDate}
                onChange={(e) => setNewMethodData(prev => ({
                  ...prev,
                  card: { ...prev.card, expiryDate: e.target.value }
                }))}
                maxLength={5}
              />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.inputLabel}>Cardholder Name</label>
              <input
                type="text"
                style={styles.input}
                placeholder="John Doe"
                value={newMethodData.card.cardholderName}
                onChange={(e) => setNewMethodData(prev => ({
                  ...prev,
                  card: { ...prev.card, cardholderName: e.target.value }
                }))}
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div style={styles.container}>
      <UserDashboardHeader
        title="Payment Methods"
        subtitle="Manage your payment options"
        showBackButton={true}
        showHomeButton={true}
      />

      <div style={styles.scrollableContent}>
        <div style={styles.content}>
          {/* Header */}
          <div style={styles.header}>
            <div style={styles.headerIcon}>💳</div>
            <h1 style={styles.headerTitle}>Payment Methods</h1>
            <p style={styles.headerSubtitle}>Manage your payment options for contributions and purchases</p>
          </div>

          {/* Add Payment Method Button */}
          <button
            style={styles.addButton}
            onClick={() => setShowAddModal(true)}
          >
            ➕ Add Payment Method
          </button>

          {/* Payment Methods List */}
          <div style={styles.methodsList}>
            {paymentMethods.length > 0 ? (
              paymentMethods.map(renderPaymentMethod)
            ) : (
              <div style={styles.emptyState}>
                <div style={styles.emptyIcon}>💳</div>
                <div style={styles.emptyText}>No payment methods added yet</div>
                <div style={styles.emptySubtext}>
                  Add a payment method to start making contributions and purchases
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Payment Method Modal */}
      {showAddModal && (
        <div style={styles.modal}>
          <div style={styles.modalContent}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Add Payment Method</h2>
              <div style={styles.modalButtons}>
                <button
                  style={{ ...styles.modalButton, ...styles.cancelButton }}
                  onClick={() => setShowAddModal(false)}
                >
                  Cancel
                </button>
                <button
                  style={{ ...styles.modalButton, ...styles.saveButton }}
                  onClick={handleAddPaymentMethod}
                  disabled={loading}
                >
                  {loading ? 'Adding...' : 'Add'}
                </button>
              </div>
            </div>

            <div style={styles.modalBody}>
              {/* Method Type Selection */}
              <h3 style={styles.sectionTitle}>Payment Method Type</h3>
              <div style={styles.methodTypesGrid}>
                {methodTypes.map(type => (
                  <div
                    key={type.id}
                    style={{
                      ...styles.methodTypeCard,
                      ...(selectedMethodType === type.id ? styles.methodTypeCardSelected : {}),
                    }}
                    onClick={() => setSelectedMethodType(type.id)}
                  >
                    <div style={styles.methodTypeIcon}>{type.icon}</div>
                    <div style={styles.methodTypeName}>{type.name}</div>
                    <div style={styles.methodTypeDescription}>{type.description}</div>
                  </div>
                ))}
              </div>

              {/* Form Fields */}
              <h3 style={styles.sectionTitle}>Payment Details</h3>
              {renderAddMethodForm()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentMethods;